import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle, X, Send, Sparkles, RotateCcw, LifeBuoy } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const newSessionId = () =>
  (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [showEscalate, setShowEscalate] = useState(false);
  const [escalateNote, setEscalateNote] = useState('');
  const [escalateStatus, setEscalateStatus] = useState('idle'); // idle | sending
  const [sessionId, setSessionId] = useState(() => {
    const existing = localStorage.getItem('advecti_chat_session');
    if (existing) return existing;
    const id = newSessionId();
    localStorage.setItem('advecti_chat_session', id);
    return id;
  });
  const scrollRef = useRef(null);
  const loadedRef = useRef(false);

  const scrollToBottom = () => {
    setTimeout(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, 50);
  };

  const loadHistory = useCallback(async (sid) => {
    const token = localStorage.getItem('advecti_token');
    if (!token) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/chat/history?session_id=${encodeURIComponent(sid)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
        scrollToBottom();
      }
    } catch {
      /* history is best-effort */
    }
  }, []);

  useEffect(() => {
    if (open && !loadedRef.current) {
      loadedRef.current = true;
      loadHistory(sessionId);
    }
  }, [open, sessionId, loadHistory]);

  const resetConversation = () => {
    const id = newSessionId();
    localStorage.setItem('advecti_chat_session', id);
    setSessionId(id);
    setMessages([]);
  };

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: text }, { role: 'assistant', content: '' }]);
    setStreaming(true);
    scrollToBottom();
    const token = localStorage.getItem('advecti_token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/chat`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, session_id: sessionId }),
      });
      if (!res.ok || !res.body) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Request failed');
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const chunks = buf.split('\n\n');
        buf = chunks.pop();
        for (const chunk of chunks) {
          if (!chunk.startsWith('data: ')) continue;
          const evt = JSON.parse(chunk.slice(6));
          if (evt.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = {
                ...copy[copy.length - 1],
                content: copy[copy.length - 1].content + evt.delta,
              };
              return copy;
            });
            scrollToBottom();
          } else if (evt.error) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: 'assistant', content: evt.error };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: 'assistant',
          content: "Sorry, I couldn't reach the assistant. Please try again.",
        };
        return copy;
      });
    } finally {
      setStreaming(false);
      scrollToBottom();
    }
  };

  const escalate = async () => {
    if (escalateStatus === 'sending') return;
    setEscalateStatus('sending');
    const token = localStorage.getItem('advecti_token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/chat/escalate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, note: escalateNote }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setShowEscalate(false);
        setEscalateNote('');
        setMessages((m) => [...m, {
          role: 'assistant',
          content: "✅ Your conversation has been sent to the Advecti team — they'll reply to your account email shortly.",
        }]);
        scrollToBottom();
      } else {
        setMessages((m) => [...m, { role: 'assistant', content: data.detail || 'Could not send — please try again.' }]);
        setShowEscalate(false);
        scrollToBottom();
      }
    } catch {
      setMessages((m) => [...m, { role: 'assistant', content: 'Could not send — please check your connection and try again.' }]);
      setShowEscalate(false);
      scrollToBottom();
    } finally {
      setEscalateStatus('idle');
    }
  };

  return (
    <>
      {/* Floating toggle button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-[60] w-14 h-14 rounded-full bg-navy text-cream shadow-xl flex items-center justify-center hover:scale-105 transition-transform"
          aria-label="Open Advecti Assistant"
          data-testid="chat-widget-button"
        >
          <MessageCircle size={24} />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="fixed bottom-6 right-6 z-[60] w-[min(24rem,calc(100vw-2rem))] h-[min(33rem,calc(100vh-6rem))] bg-white rounded-2xl shadow-2xl border border-navy/10 flex flex-col overflow-hidden"
          data-testid="chat-widget-panel"
        >
          {/* Header */}
          <div className="bg-navy text-cream px-4 py-3 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <Sparkles size={17} />
              <div>
                <p className="font-bold text-sm leading-tight">Advecti Assistant</p>
                <p className="text-cream/60 text-[11px] leading-tight">Ask me anything about your campaign</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setShowEscalate((v) => !v)}
                title="Email support"
                className="p-1.5 rounded-lg hover:bg-cream/10 transition"
                data-testid="chat-escalate-toggle"
              >
                <LifeBuoy size={15} />
              </button>
              <button
                onClick={resetConversation}
                title="New conversation"
                className="p-1.5 rounded-lg hover:bg-cream/10 transition"
                data-testid="chat-reset"
              >
                <RotateCcw size={15} />
              </button>
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-lg hover:bg-cream/10 transition"
                data-testid="chat-close"
              >
                <X size={17} />
              </button>
            </div>
          </div>

          {/* Escalate to human support */}
          {showEscalate && (
            <div className="border-b border-navy/10 bg-amber-50/70 px-4 py-3 shrink-0" data-testid="chat-escalate-panel">
              <p className="text-xs text-navy/70 mb-2 font-semibold flex items-center gap-1.5">
                <LifeBuoy size={13} /> Send this conversation to the Advecti team
              </p>
              <textarea
                value={escalateNote}
                onChange={(e) => setEscalateNote(e.target.value)}
                placeholder="Optional: add a note about what you need…"
                rows={2}
                className="w-full px-3 py-2 border border-navy/20 rounded-lg text-xs resize-none mb-2"
                data-testid="chat-escalate-note"
              />
              <div className="flex gap-2">
                <button
                  onClick={escalate}
                  disabled={escalateStatus === 'sending'}
                  className="px-3 py-1.5 bg-navy text-cream rounded-lg text-xs font-semibold disabled:opacity-50"
                  data-testid="chat-escalate-send"
                >
                  {escalateStatus === 'sending' ? 'Sending…' : 'Email support'}
                </button>
                <button
                  onClick={() => setShowEscalate(false)}
                  className="px-3 py-1.5 text-xs text-navy/60 hover:text-navy"
                  data-testid="chat-escalate-cancel"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-cream-light/40" data-testid="chat-messages">
            {messages.length === 0 && (
              <div className="text-center text-navy/50 text-sm mt-8 space-y-3">
                <Sparkles className="mx-auto text-navy/30" size={28} />
                <p>Hi! I can help with your campaign, impressions, billing, integrations, and more.</p>
                <div className="flex flex-col gap-1.5 items-center">
                  {['How are my impressions calculated?', 'How do I connect Shopify?', 'What do the driver progress bars mean?'].map((s) => (
                    <button
                      key={s}
                      onClick={() => setInput(s)}
                      className="text-xs bg-white border border-navy/15 rounded-full px-3 py-1.5 text-navy/70 hover:bg-navy hover:text-cream transition"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap leading-relaxed ${
                    m.role === 'user'
                      ? 'bg-navy text-cream rounded-br-md'
                      : 'bg-white border border-navy/10 text-navy rounded-bl-md'
                  }`}
                >
                  {m.content || (
                    <span className="inline-flex gap-1 items-center py-1">
                      <span className="w-1.5 h-1.5 bg-navy/40 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 bg-navy/40 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-1.5 h-1.5 bg-navy/40 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Input */}
          <div className="border-t border-navy/10 p-3 flex items-center gap-2 shrink-0 bg-white">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && send()}
              placeholder="Type a message…"
              className="flex-1 px-3 py-2 border border-navy/20 rounded-xl text-sm focus:outline-none focus:border-navy/50"
              data-testid="chat-input"
            />
            <button
              onClick={send}
              disabled={streaming || !input.trim()}
              className="w-9 h-9 rounded-xl bg-navy text-cream flex items-center justify-center disabled:opacity-40 hover:bg-navy/90 transition"
              data-testid="chat-send"
            >
              <Send size={15} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
